import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { Action, SmartOrderState } from './hooks/use-smart-order-reducer';
import { ApolloQueryResult } from '@apollo/client';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { Order_SmartOrderQuery } from '@/types';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { SmartOrderResultsList } from './smart-order-results-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, Text, View } from 'react-native';
import { useCreateSmartOrder } from '@/hooks/use-create-smart-order';
import { useNavigation } from '@react-navigation/native';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  dispatch: React.Dispatch<Action>;
  getSmartOrderResults: (
    items: string,
    isRestrictedSearch: boolean,
  ) => Promise<ApolloQueryResult<Order_SmartOrderQuery>>;
  goBack: () => void;
  onClose: () => void;
  state: SmartOrderState;
}

type Navigation = StackNavigationProp<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.NewOrder
>;

export const SmartOrderResults: React.FC<Props> = ({
  dispatch,
  getSmartOrderResults,
  goBack,
  onClose,
  state,
}) => {
  const { navigate } = useNavigation<Navigation>();
  const { showErrorToast } = useToasts();

  const { onAddToCart, addToCartLoading } = useCreateSmartOrder({
    state,
  });

  const handleAddToCart = async () => {
    try {
      PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
        entryPoint: 'smart_order_results_add_to_cart_button',
      });
      const added = await onAddToCart('APP_ORDER');
      if (!added) {
        // updateCart already shows an error toast
        return;
      }

      navigate(APP_CONTAINER_ROUTES.NewOrder);
      onClose();
    } catch {
      showErrorToast();
    }
  };

  const hasRequestedItems = useMemo(() => {
    return Object.values(state.quantityMap).some((value) => value > 0);
  }, [state.quantityMap]);

  return state.groupedResults.length > 0 ? (
    <View style={styles.base}>
      <SmartOrderResultsList
        dispatch={dispatch}
        getSmartOrderResults={getSmartOrderResults}
        listFooterStyle={styles.resetMargin}
        state={state}
      />
      {hasRequestedItems ? (
        <Button
          loading={addToCartLoading}
          onPress={handleAddToCart}
          style={styles.addToCartButton}
          trackingData={null}
        >
          {i18n.t('add_items_to_order')}
        </Button>
      ) : null}
    </View>
  ) : (
    <View style={styles.noResults}>
      <Text style={[TYPOGRAPHY.p, styles.noResultsText]}>
        {i18n.t('smart_order.no_results')}
      </Text>
      <Button
        onPress={goBack}
        trackingData={{ name: 'smart_order.empty_results_back_button' }}
      >
        {i18n.t('back')}
      </Button>
    </View>
  );
};

const styles = StyleSheet.create({
  addToCartButton: {
    borderRadius: 0,
  },
  base: {
    flex: 1,
    // Allow full bleed since screen padding is
    // applied by both modal and list container
    marginHorizontal: -SCREEN_PADDING * 2,
  },
  noResults: {
    flex: 1,
    gap: 16,
    marginTop: 56,
  },
  noResultsText: {
    lineHeight: 24,
    textAlign: 'center',
  },
  resetMargin: {
    marginHorizontal: SCREEN_PADDING * 2,
  },
});
